import {
  ThemeCustomRichText as CustomRichText,
  ThemeRichTextElement as RichTextElement,
  ThemeRichTextNode as RichTextNode,
  ThemeRichTextNodeArray as RichTextNodeArray,
  ThemeRichTextNodeArray,
} from '@nucleus/types/web';
import { upgradeNodes } from '@nucleus/web-rich-text-upgrade';
import React from 'react';
import { useRichTextRegistryProvider } from './RichTextRegistry';

const isElement = (node: RichTextNode): node is RichTextElement => (node as RichTextElement).type !== undefined;

const HighlightGroupContext = React.createContext(false);

const isNodeHighlighted = (node: RichTextNode): boolean => {
  if (isElement(node)) {
    return node.children.length > 0 && node.children.every(isNodeHighlighted);
  }
  return node.highlight !== undefined;
};

interface HighlightNodeGroup {
  isHighlighted: boolean;
  nodes: Array<RichTextNode>;
}

const groupContiguousByHighlight = (nodes: RichTextNodeArray): Array<HighlightNodeGroup> => {
  const groups: Array<HighlightNodeGroup> = [];
  for (const node of nodes) {
    const isHighlighted = isNodeHighlighted(node);
    const lastGroup = groups[groups.length - 1];
    if (lastGroup !== undefined && lastGroup.isHighlighted === isHighlighted) {
      lastGroup.nodes.push(node);
    } else {
      groups.push({ isHighlighted: isHighlighted, nodes: [node] });
    }
  }
  return groups;
};

interface RichTextProps {
  nodes: RichTextNodeArray | string;
}

export const RichText = (props: RichTextProps): JSX.Element => {
  if (typeof props.nodes === 'string') {
    return <>{props.nodes}</>;
  }

  return <RenderNodes nodes={upgradeNodes(props.nodes)} />;
};

interface RenderNodesProps {
  nodes: RichTextNodeArray;
}

const renderNode = (node: RichTextNode, index: number, siblings: RichTextNodeArray): JSX.Element => {
  if (isElement(node)) {
    return <Element key={index} node={node} />;
  }
  return <Leaf key={index} node={node} siblings={siblings} />;
};

const RenderNodes = (props: RenderNodesProps): JSX.Element => {
  const isInHighlightGroup = React.useContext(HighlightGroupContext);
  const { getTextByType } = useRichTextRegistryProvider();

  const hasLeafNodes = props.nodes.some((node) => !isElement(node));

  if (isInHighlightGroup || !hasLeafNodes) {
    return <>{props.nodes.map((node, index) => renderNode(node, index, props.nodes))}</>;
  }

  const groups = groupContiguousByHighlight(props.nodes);

  return (
    <>
      {groups.map((group, groupIndex) => {
        if (group.isHighlighted) {
          const Highlight = getTextByType('highlight');
          return (
            <Highlight key={groupIndex}>
              <HighlightGroupContext.Provider value={true}>
                {group.nodes.map((node, nodeIndex) => renderNode(node, nodeIndex, props.nodes))}
              </HighlightGroupContext.Provider>
            </Highlight>
          );
        }

        return (
          <React.Fragment key={groupIndex}>
            {group.nodes.map((node, nodeIndex) => renderNode(node, nodeIndex, props.nodes))}
          </React.Fragment>
        );
      })}
    </>
  );
};

interface ElementProps {
  node: RichTextElement;
}

const Element = ({ node: element }: ElementProps): JSX.Element | null => {
  const { getElementByType } = useRichTextRegistryProvider();

  const ElementComponent = getElementByType(element.type);

  if (ElementComponent === undefined) {
    return null;
  }

  return (
    <ElementComponent node={element}>
      <RenderNodes nodes={element.children} />
    </ElementComponent>
  );
};

interface LeafProps {
  node: CustomRichText;
  siblings: ThemeRichTextNodeArray | string;
}

const Leaf = ({ node: leaf, siblings }: LeafProps): JSX.Element => {
  const { getTextByType } = useRichTextRegistryProvider();
  const isInHighlightGroup = React.useContext(HighlightGroupContext);

  if (leaf.text === undefined) {
    return <></>;
  }

  const isEmptyText = leaf.text === '';
  const isOnlyLeaf = siblings.length === 1;
  if (isEmptyText && isOnlyLeaf) {
    return <br />;
  }

  let textElement = (
    <>
      {leaf.text.split('\n').map((text, index) => {
        if (index === 0) {
          return <React.Fragment key={index}>{text}</React.Fragment>;
        }

        return (
          <React.Fragment key={index}>
            <br />
            {text}
          </React.Fragment>
        );
      })}
    </>
  );

  if (leaf.bold === true) {
    const Bold = getTextByType('bold');
    textElement = <Bold>{textElement}</Bold>;
  }

  if (leaf.italic === true) {
    const Italic = getTextByType('italic');
    textElement = <Italic>{textElement}</Italic>;
  }

  if (leaf.underline === true) {
    const Underline = getTextByType('underline');
    textElement = <Underline>{textElement}</Underline>;
  }

  if (leaf.strike === true) {
    const Strike = getTextByType('strike');
    textElement = <Strike>{textElement}</Strike>;
  }

  if (leaf.highlight !== undefined && !isInHighlightGroup) {
    const Highlight = getTextByType('highlight');
    textElement = <Highlight>{textElement}</Highlight>;
  }

  return textElement;
};
